import { PaguzLogo, cn } from '@paguz/ui';
import { Link, useRouter } from '@tanstack/react-router';
import {
  ArrowLeftIcon,
  HomeIcon,
  QrCodeIcon,
  ReceiptTextIcon,
  SparklesIcon,
  TriangleAlertIcon,
  UserRoundIcon,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { config } from '../config.ts';

/** Marco móvil: 360–480 px, centrado en desktop. */
export function MobileFrame({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-dvh justify-center bg-muted">
      <div className="relative flex min-h-dvh w-full max-w-[480px] flex-col bg-background shadow-sm">
        {config.demoMode && <DemoBanner />}
        {children}
      </div>
    </div>
  );
}

export function DemoBanner() {
  return (
    <div
      role="note"
      className="sticky top-0 z-30 flex items-center justify-center gap-2 bg-warning-soft px-4 py-1.5 text-center text-sm font-medium text-warning-soft-foreground"
    >
      <TriangleAlertIcon className="size-4 shrink-0" aria-hidden />
      <span>
        Entorno de demostración — sin dinero real ·{' '}
        <Link to="/guia" className="font-semibold underline underline-offset-2">
          Guía
        </Link>
      </span>
    </div>
  );
}

const NAV_ITEMS = [
  { to: '/', label: 'Inicio', icon: HomeIcon, exact: true },
  { to: '/asesor', label: 'Asesor IA', icon: SparklesIcon, exact: false },
  { to: '/pagar', label: 'Pagar', icon: QrCodeIcon, exact: false, primary: true },
  { to: '/movimientos', label: 'Movimientos', icon: ReceiptTextIcon, exact: false },
  { to: '/perfil', label: 'Perfil', icon: UserRoundIcon, exact: false },
] as const;

export function BottomNav() {
  return (
    <nav
      aria-label="Navegación principal"
      className="fixed inset-x-0 bottom-0 z-30 mx-auto w-full max-w-[480px] border-t bg-card pb-[env(safe-area-inset-bottom)]"
    >
      <ul className="grid grid-cols-5">
        {NAV_ITEMS.map((item) => {
          const Icon = item.icon;
          return (
            <li key={item.to}>
              <Link
                to={item.to}
                activeOptions={{ exact: item.exact }}
                className="group flex h-16 flex-col items-center justify-center gap-0.5 text-xs font-medium text-muted-foreground data-[status=active]:text-primary"
              >
                {'primary' in item ? (
                  <span className="-mt-5 flex size-12 items-center justify-center rounded-full bg-highlight text-highlight-foreground shadow-md ring-4 ring-card">
                    <Icon className="size-6" aria-hidden />
                  </span>
                ) : (
                  <Icon className="size-6" aria-hidden />
                )}
                <span>{item.label}</span>
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

export function PageHeader({
  title,
  back,
  action,
  className,
}: {
  title: string;
  /** Muestra "Volver" (historial o la ruta indicada). */
  back?: boolean | string;
  action?: ReactNode;
  className?: string;
}) {
  const router = useRouter();
  return (
    <header className={cn('mb-4 flex min-h-11 items-center gap-2', className)}>
      {back !== undefined && back !== false && (
        <button
          type="button"
          aria-label="Volver"
          onClick={() => {
            if (typeof back === 'string') void router.navigate({ to: back });
            else router.history.back();
          }}
          className="-ml-2 flex size-11 items-center justify-center rounded-full text-foreground hover:bg-accent"
        >
          <ArrowLeftIcon className="size-6" aria-hidden />
        </button>
      )}
      <h1 className="flex-1 text-2xl text-foreground">{title}</h1>
      {action}
    </header>
  );
}

export function BrandHeader({ children }: { children?: ReactNode }) {
  return (
    <header className="mb-4 flex min-h-11 items-center justify-between gap-3">
      <PaguzLogo className="h-7 w-auto" />
      {children}
    </header>
  );
}
